import { useEffect, useRef, useState } from "react";
import {
  X,
  Upload,
  Pencil,
  ImagePlus,
  ArrowUpRight,
  Check,
  LoaderCircle,
} from "lucide-react";
import { creativeApi } from "../config/api";
import { formatDate } from "./CreativeGrid";
import StatusBadge, { STATUSES } from "./StatusBadge";
export default function CreativeModal({ record, onClose, onUpdate, notify }) {
  const [editing, setEditing] = useState(!record.creativeUrl);
  const [caption, setCaption] = useState(record.caption);
  const [keywords, setKeywords] = useState(record.seoKeywords.join(", "));
  const [url, setUrl] = useState(record.creativeUrl);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dialog = useRef(null);
  const uploadInput = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current.showModal();
    return () => {
      previous?.focus();
    };
  }, []);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  function chooseFile(selected) {
    setError("");
    if (!selected) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(selected.type) ||
      selected.size > 5 * 1024 * 1024
    ) {
      setError("Choose a JPG, PNG, or WebP image smaller than 5 MB.");
      return;
    }
    setFile(selected);
  }
  async function changeStatus(status) {
    setBusy(true);
    setError("");
    try {
      const data = await creativeApi.update(record._id, { status });
      onUpdate(data.creative);
      notify(`Creative ${status.toLowerCase()}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function save(event) {
    event.preventDefault();
    const tags = [
      ...new Set(
        keywords
          .split(",")
          .map((k) => k.trim())
          .filter(Boolean),
      ),
    ];
    if (
      tags.length < 3 ||
      tags.length > 20 ||
      tags.some((k) => k.length > 60)
    ) {
      setError(
        "Add 3–20 keywords, separated by commas (up to 60 characters each).",
      );
      return;
    }
    if (!file && !url) {
      setError("Select an image or enter its URL.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      let data;
      if (file) {
        const form = new FormData();
        form.append("image", file);
        form.append("caption", caption);
        form.append("seoKeywords", JSON.stringify(tags));
        data = await creativeApi.upload(record._id, form);
      } else {
        const changes = { caption, seoKeywords: tags };
        if (url !== record.creativeUrl) changes.creativeUrl = url;
        data = await creativeApi.update(record._id, changes);
      }
      onUpdate(data.creative);
      setUrl(data.creative.creativeUrl);
      setFile(null);
      setEditing(false);
      notify("Creative saved. Looking good!");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function approvedUpload(selected) {
    if (!selected) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(selected.type) ||
      selected.size > 5 * 1024 * 1024
    ) {
      setError("Choose a JPG, PNG, or WebP image smaller than 5 MB.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("image", selected);
      const data = await creativeApi.upload(record._id, form, true);
      onUpdate(data.creative);
      setUrl(data.creative.creativeUrl);
      notify("Creative uploaded successfully");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="creative-dialog"
      onCancel={(e) => {
        if (busy) e.preventDefault();
        else onClose();
      }}
      onClick={(e) => {
        if (e.target === dialog.current && !busy) onClose();
      }}
      aria-labelledby="creative-title"
    >
      <div className="modal-header">
        <div>
          <span className="eyebrow">YOUR CONTENT, IN FOCUS</span>
          <h2 id="creative-title">
            {editing
              ? record.creativeUrl
                ? "Edit creative"
                : "A new idea starts here"
              : "Creative review"}
            <span>{formatDate(record.date)}</span>
          </h2>
        </div>
        <button
          className="icon-button"
          aria-label="Close preview"
          onClick={onClose}
          disabled={busy}
        >
          <X size={21} />
        </button>
      </div>
      <div className="modal-body">
        <div className="modal-art">
          {preview || (editing ? url : record.creativeUrl) ? (
            <img
              src={preview || (editing ? url : record.creativeUrl)}
              alt="Selected creative preview"
            />
          ) : (
            <div className="empty-preview">
              <ImagePlus size={42} strokeWidth={1} />
              <h3>Make something worth sharing.</h3>
              <p>Your creative will appear here.</p>
            </div>
          )}
          <span className="art-watermark">
            forma studio <ArrowUpRight size={15} />
          </span>
        </div>
        <div className="modal-info">
          {editing ? (
            <form id="creative-form" onSubmit={save}>
              <label className="upload-zone">
                <Upload size={22} />
                <strong>{file ? file.name : "Choose your creative"}</strong>
                <small>JPG, PNG or WebP · up to 5 MB</small>
                <input
                  aria-label="Choose creative image"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => chooseFile(e.target.files[0])}
                  disabled={busy}
                />
              </label>
              <label>
                Or use an image URL
                <input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={url.startsWith("/") ? "" : url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setFile(null);
                  }}
                  disabled={busy}
                />
              </label>
              <label>
                Caption
                <textarea
                  required
                  maxLength={2200}
                  rows={4}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Tell the story behind your creative…"
                  disabled={busy}
                />
              </label>
              <label>
                SEO Keywords
                <textarea
                  aria-label="SEO Keywords"
                  aria-describedby="keyword-help"
                  required
                  rows={2}
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="slow living, home inspiration, design"
                  disabled={busy}
                />
                <small id="keyword-help">At least 3 keywords, separated by commas.</small>
              </label>
            </form>
          ) : (
            <>
              <div className="information-top">
                <span className="eyebrow">THE STORY BEHIND THE POST</span>
                <StatusBadge status={record.status} />
              </div>
              <h3>Caption</h3>
              <p className="caption-text">{record.caption}</p>
              <h3>SEO Keywords</h3>
              <div className="keyword-list">
                {record.seoKeywords.map((keyword) => (
                  <span key={keyword}>#{keyword}</span>
                ))}
              </div>
              <button
                className="text-button edit-action"
                onClick={() => {
                  setCaption(record.caption);
                  setKeywords(record.seoKeywords.join(", "));
                  setUrl(record.creativeUrl);
                  setEditing(true);
                  setError("");
                }}
                disabled={busy}
              >
                <Pencil size={15} /> Edit / Replace Creative
              </button>
              <div className="review-control">
                <label htmlFor="creative-status">Review status</label>
                <p>A little feedback moves good ideas forward.</p>
                <select
                  id="creative-status"
                  value={record.status}
                  onChange={(e) => changeStatus(e.target.value)}
                  disabled={busy}
                >
                  {STATUSES.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
                {record.status === "Approved" && (
                  <>
                    <button
                      className="button primary full"
                      onClick={() => uploadInput.current.click()}
                      disabled={busy}
                    >
                      <Upload size={17} /> Upload Creative
                    </button>
                    <input
                      hidden
                      ref={uploadInput}
                      type="file"
                      aria-label="Upload approved creative"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        approvedUpload(e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          {busy && (
            <p className="saving" role="status">
              <LoaderCircle className="spin" size={15} /> Saving your changes…
            </p>
          )}
        </div>
      </div>
      <div className="modal-footer">
        <span>
          <span className="live-dot" />{" "}
          {editing
            ? "Every great story starts with an idea."
            : "One creative. In sync across every view."}
        </span>
        {editing ? (
          <div className="button-row">
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => {
                setFile(null);
                setError("");
                record.creativeUrl ? setEditing(false) : onClose();
              }}
            >
              Cancel
            </button>
            <button
              className="button primary"
              type="submit"
              form="creative-form"
              disabled={busy}
            >
              <Check size={16} />
              {busy ? "Saving…" : "Save creative"}
            </button>
          </div>
        ) : (
          <button
            className="button secondary"
            onClick={onClose}
            disabled={busy}
          >
            Done
          </button>
        )}
      </div>
    </dialog>
  );
}
